import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DraftChip } from '../DraftChip/DraftChip';
import { FeedbackButtons } from '../FeedbackButtons';

export interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  toolTrace?: Array<{ name: string; uiLabel?: string; done: boolean }>;
  draftRefs?: Array<{ pluginId: string; entityType: string; entityId: string }>;
  threadId?: string;
  messageId?: string;
}


const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  p: ({ children }) => <p style={{ margin: '0 0 6px 0' }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: '4px 0 6px 0', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0 6px 0', paddingLeft: 18 }}>{children}</ol>,
  li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
  code: ({ children, ...props }) => {
    const isInline = !(props as { inline?: boolean }).inline === false;
    return isInline ? (
      <code
        style={{
          background: 'var(--muted)',
          padding: '1px 4px',
          borderRadius: 3,
          fontFamily: 'ui-monospace, monospace',
          fontSize: '0.92em',
        }}
      >
        {children}
      </code>
    ) : (
      <pre
        style={{
          background: 'var(--muted)',
          padding: 8,
          borderRadius: 6,
          overflow: 'auto',
          fontFamily: 'ui-monospace, monospace',
          fontSize: '0.85em',
          margin: '6px 0',
        }}
      >
        <code>{children}</code>
      </pre>
    );
  },
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
      {children}
    </a>
  ),
  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
  h1: ({ children }) => <h3 style={{ margin: '6px 0 4px 0', fontSize: 14, fontWeight: 600 }}>{children}</h3>,
  h2: ({ children }) => <h3 style={{ margin: '6px 0 4px 0', fontSize: 14, fontWeight: 600 }}>{children}</h3>,
  h3: ({ children }) => <h4 style={{ margin: '6px 0 4px 0', fontSize: 13, fontWeight: 600 }}>{children}</h4>,
  h4: ({ children }) => <h4 style={{ margin: '6px 0 4px 0', fontSize: 13, fontWeight: 600 }}>{children}</h4>,
  table: ({ children }) => (
    <table style={{ borderCollapse: 'collapse', fontSize: '0.95em', margin: '6px 0' }}>{children}</table>
  ),
  th: ({ children }) => (
    <th style={{ border: '1px solid var(--border)', padding: '3px 6px', textAlign: 'left', background: 'var(--muted)' }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{ border: '1px solid var(--border)', padding: '3px 6px' }}>{children}</td>
  ),
};

export const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  toolTrace,
  draftRefs,
  threadId,
  messageId,
}) => {
  const isUser = role === 'user';
  return (
    <div
      data-testid={`chat-msg-${role}`}
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        background: isUser ? 'var(--primary)' : 'var(--muted)',
        color: isUser ? 'var(--primary-foreground)' : 'inherit',
        padding: '8px 10px',
        borderRadius: 8,
        maxWidth: '85%',
        lineHeight: 1.45,
      }}
    >
      {isUser ? (
        <div style={{ whiteSpace: 'pre-wrap' }}>{content}</div>
      ) : (
        <div className="ai-md">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {content}
          </ReactMarkdown>
        </div>
      )}

      {draftRefs && draftRefs.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {draftRefs.map((d) => <DraftChip key={d.entityId} {...d} />)}
        </div>
      )}
      {role === 'assistant' && threadId && messageId && (
        <div
          data-testid="chat-msg-feedback"
          style={{ marginTop: 4, display: 'flex', justifyContent: 'flex-end' }}
        >
          <FeedbackButtons
            targetKind="chat_message"
            threadId={threadId}
            messageId={messageId}
            snapshot={{ content, toolTrace, draftRefs }}
          />
        </div>
      )}
    </div>
  );
};
